<script setup lang="ts">
import { ElInput } from 'element-plus'
import { Loader2 } from 'lucide-vue-next'
import { ref } from 'vue'

interface Emits {
  (e: 'send', message: string): void
}

interface Props {
  isLoading?: boolean
}

defineProps<Props>()

const emit = defineEmits<Emits>()

const message = ref('')
const isFocused = ref(false)
const chatInputRef = ref<InstanceType<typeof ElInput>>()

function handleSend() {
  const trimmedMessage = message.value.trim()
  if (trimmedMessage) {
    emit('send', trimmedMessage)
    message.value = ''
  }
}

function focus() {
  chatInputRef.value?.focus()
}

defineExpose({
  focus,
})
</script>

<template>
  <div
    class="flex items-center pr-3 gap-2 m-5 sm:m-0 border border-border-light-2 rounded-xl transition-200 focus-within:border-blue-500"
  >
    <ElInput
      ref="chatInputRef"
      v-model="message"
      type="textarea"
      :rows="1"
      :autosize="{ minRows: 1, maxRows: 4 }"
      :placeholder="$t('ask_ai_placeholder')"
      class="chat-input placeholder:text-text-secondary font-normal"
      @keydown.enter.prevent="handleSend"
      @focus="isFocused = true"
      @blur="isFocused = false"
    />
    <el-button
      type="primary"
      circle
      size="large"
      class="send-button disabled:bg-[#C0C4CC] disabled:cursor-not-allowed"
      :class="{
        'opacity-0': !isFocused,
        'bg-blue-light-2': message.trim(),
        'bg-gray-400': !message.trim(),
      }"
      :disabled="!message.trim() || isLoading"
      @click="handleSend"
    >
      <i v-if="!isLoading" class="icon-arrow-up text-base text-white" />
      <Loader2 v-else class="w-5 h-5 animate-spin" />
    </el-button>
  </div>
</template>

<style scoped>
.chat-input :deep(.el-textarea__inner) {
  border: none !important;
  outline: none !important;
  padding: 16px 12px;
  font-size: 14px;
  resize: none;
  box-shadow: none;
  color: #303133;
  line-height: 22px;
}

.send-button {
  width: 36px;
  height: 36px;
  box-shadow: 0px 0px 6px 0px #0000001f;
  border: none;
  transition:
    opacity 0.2s ease,
    background-color 0.2s ease;
}

.send-button:hover {
  opacity: 0.8;
}
</style>
